import React from "react";
import { Search, X } from "lucide-react";

const LIVE_WINDOW_MIN = 15;   // a scheduled race is treated as live this long after its start time
const NOT_RUN_GRACE_MIN = 30; // after this, a never-raced past race is "Not run"
const STALE_LIVE_HOURS = 3;   // a "racing" status this old never closed out - not really live

// Pure UI grouping derived from existing contest fields - never written back.
export function badgeOf(c, now = Date.now()) {
  const s = c.status;
  const start = c.start_time ? new Date(c.start_time).getTime() : null;
  if (s === "completed") return "completed";
  if (s === "cancelled" || s === "refunded") return "cancelled";
  if (s === "racing" || s === "live") return start && now > start + STALE_LIVE_HOURS * 3600000 ? "not_run" : "live";
  if (start && now > start + NOT_RUN_GRACE_MIN * 60000) return "not_run";
  if (s === "hold") return "draft";
  if (start && now >= start && now <= start + LIVE_WINDOW_MIN * 60000) return "live";
  return "upcoming";
}

export const VIEW_OF = { live: "current", upcoming: "current", draft: "drafts", completed: "previous", cancelled: "previous", not_run: "previous" };

export const BADGES = {
  live: { label: "Live", cls: "text-red-500 border-red-500/60 bg-red-500/10" },
  upcoming: { label: "Upcoming", cls: "text-sky-500 border-sky-400/40 bg-sky-400/5" },
  draft: { label: "Draft", cls: "text-amber-500 border-amber-400/40 bg-amber-400/5" },
  completed: { label: "Completed", cls: "text-green-500 border-green-500/40 bg-green-500/5" },
  cancelled: { label: "Cancelled", cls: "text-race-sub border-race-line bg-race-bg" },
  not_run: { label: "Not run", cls: "text-race-muted border-race-line bg-race-bg" },
};

export const StatusBadge = ({ kind, testid }) => {
  const b = BADGES[kind] || BADGES.upcoming;
  return (
    <span className={`inline-flex items-center gap-1 border rounded-sm px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest shrink-0 ${b.cls}`} data-testid={testid}>
      {kind === "live" && <span className="relative flex w-1.5 h-1.5"><span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" /><span className="relative w-1.5 h-1.5 rounded-full bg-red-500" /></span>}
      {b.label}
    </span>
  );
};

const time = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
// "Today at 3:00 PM" / "Tomorrow at 9:45 AM" / "Starts in 2 days" / "Started 5 min ago"
export function relTime(iso, now = Date.now()) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = d.getTime() - now;
  if (diff < 0) return diff > -3600000 ? `Started ${Math.max(1, Math.round(-diff / 60000))} min ago` : "";
  if (diff < 3600000) return `Starts in ${Math.max(1, Math.round(diff / 60000))} min`;
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(d) - day(new Date(now))) / 86400000);
  if (days === 0) return `Today at ${time(d)}`;
  if (days === 1) return `Tomorrow at ${time(d)}`;
  if (days < 14) return `Starts in ${days} days`;
  return "";
}

const TABS = [["current", "Current & Planned"], ["previous", "Previous"], ["drafts", "Drafts"], ["all", "All Races"]];
const sel = "bg-race-bg border border-race-line rounded-sm px-2 py-1.5 text-xs text-race-txt outline-none focus:border-race-txt";

export const ContestToolbar = ({ view, setView, counts, q, setQ, filters, setFilters, raceTypes, sponsors }) => (
  <div className="bg-race-surface border border-race-line rounded-sm p-2 space-y-2" data-testid="cm-toolbar">
    <div className="flex flex-col lg:flex-row lg:items-center gap-2">
      <div className="flex gap-1 overflow-x-auto no-scrollbar -mx-0.5 px-0.5" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`cm-tab-${k}`}
            className={`whitespace-nowrap px-3 py-1.5 rounded-sm text-xs font-mono transition-colors ${view === k ? "bg-race-txt text-race-bg font-semibold" : "text-race-sub hover:text-race-txt hover:bg-race-bg"}`}>
            {l}{counts[k] ? <span className={`ml-1.5 ${view === k ? "opacity-70" : "text-race-muted"}`} data-testid={`cm-count-${k}`}>({counts[k]})</span> : null}
          </button>
        ))}
      </div>
      <div className="relative lg:ml-auto lg:w-64">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-race-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, sponsor, ID, URL…" data-testid="cm-search"
          className="w-full bg-race-bg border border-race-line rounded-sm pl-8 pr-7 py-1.5 text-xs text-race-txt outline-none focus:border-race-txt" />
        {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-race-muted hover:text-race-txt" data-testid="cm-search-clear"><X size={12} /></button>}
      </div>
    </div>
    {view === "all" && (
      <div className="flex flex-wrap items-center gap-2 border-t border-race-line pt-2" data-testid="cm-filters">
        <select className={sel} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="cm-filter-status">
          <option value="">All statuses</option>
          {Object.entries(BADGES).map(([k, b]) => <option key={k} value={k}>{b.label}</option>)}
        </select>
        <select className={sel} value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="cm-filter-type">
          <option value="">All race types</option>
          {raceTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {sponsors.length > 0 && (
          <select className={sel} value={filters.sponsor} onChange={(e) => setFilters({ ...filters, sponsor: e.target.value })} data-testid="cm-filter-sponsor">
            <option value="">All sponsors</option>
            {sponsors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <span className="text-[10px] uppercase tracking-widest text-race-muted ml-1">From</span>
        <input type="date" className={sel} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="cm-filter-from" />
        <span className="text-[10px] uppercase tracking-widest text-race-muted">To</span>
        <input type="date" className={sel} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="cm-filter-to" />
        {Object.values(filters).some(Boolean) && (
          <button onClick={() => setFilters({ status: "", type: "", sponsor: "", from: "", to: "" })} data-testid="cm-filter-reset"
            className="text-xs font-mono text-race-sub hover:text-race-txt underline-offset-2 hover:underline">Reset</button>
        )}
      </div>
    )}
  </div>
);
